import piexif from 'piexifjs';

/**
 * Normalize JPEG orientation using EXIF if present.
 * Returns a blob: URL with pixels physically rotated (EXIF removed).
 */
export async function fixImageOrientation(file) {
  // Only handle JPEG; return as-is for others
  const mime = (file.type || '').toLowerCase();
  if (!mime.startsWith('image/jpeg')) {
    return URL.createObjectURL(file);
  }

  // ---- Read EXIF orientation safely
  const buf = await file.arrayBuffer();
  const u8 = new Uint8Array(buf);
  // safer binary construction (avoid reduce + call stack issues)
  let binary = '';
  for (let i = 0; i < u8.length; i++) binary += String.fromCharCode(u8[i]);

  let orientation = 1;
  try {
    const exif = piexif.load(binary);
    orientation = exif?.['0th']?.[piexif.ImageIFD.Orientation] ?? 1;
  } catch (e) {
    console.warn('EXIF parse failed; proceeding as orientation=1', e);
  }
  console.log('Detected EXIF Orientation =', orientation);

  // If normal -> just pass through
  if (orientation === 1) {
    return URL.createObjectURL(file);
  }

  // ---- Decode image (prefer Image() for consistent behavior)
  const objectUrl = URL.createObjectURL(file);
  const img = await new Promise((resolve, reject) => {
    const _img = new Image();
    _img.onload = () => resolve(_img);
    _img.onerror = reject;
    // If you fetch remote URLs, you may need this:
    // _img.crossOrigin = 'anonymous';
    _img.src = objectUrl;
  });

  // ---- Prepare canvas with swapped dims when needed
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const swap = orientation > 4; // 5..8 swap width/height
  canvas.width  = swap ? img.height : img.width;
  canvas.height = swap ? img.width  : img.height;

  // ---- Apply the standard EXIF transforms
  // (These assume the decoded pixels are in their original stored orientation)
  switch (orientation) {
    case 2: // mirror horizontal
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      break;
    case 3: // rotate 180
      ctx.translate(canvas.width, canvas.height);
      ctx.rotate(Math.PI);
      break;
    case 4: // mirror vertical
      ctx.translate(0, canvas.height);
      ctx.scale(1, -1);
      break;
    case 5: // mirror horizontal + rotate 270 CW
      ctx.rotate(0.5 * Math.PI);
      ctx.scale(1, -1);
      break;
    case 6: // rotate 90 CW
      ctx.rotate(0.5 * Math.PI);
      ctx.translate(0, -canvas.width);
      break;
    case 7: // mirror horizontal + rotate 90 CW
      ctx.rotate(0.5 * Math.PI);
      ctx.translate(canvas.height, -canvas.width);
      ctx.scale(-1, 1);
      break;
    case 8: // rotate 270 CW (90 CCW)
      ctx.rotate(-0.5 * Math.PI);
      ctx.translate(-canvas.height, 0);
      break;
    default:
      break;
  }

  ctx.drawImage(img, 0, 0);
  URL.revokeObjectURL(objectUrl); // cleanup

  // ---- Emit new blob URL (EXIF removed; pixels normalized)
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
  return URL.createObjectURL(blob);
}
